import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@contexts/AuthContext';
import { useI18n } from '@/i18n';
import api from '@utils/api';
import logoWordmark from '../assets/logo-glk-wordmark.png';
import { IconHome, IconUser, IconFile, IconShield, IconLogout, IconChat, IconTicket, IconBell, IconShirt, IconBox, IconInbox, IconCheckSquare, IconGraduation, IconCalendar } from './icons';

const NAV = [
  { to: '/',          key: 'home',      Icon: IconHome,   end: true },
  { to: '/profile',   key: 'profile',   Icon: IconUser },
  { to: '/training',  key: 'training',  Icon: IconGraduation },
  { to: '/absences',  key: 'absences',  Icon: IconCalendar },
  { to: '/assistant', key: 'assistant', Icon: IconChat },
  { to: '/tickets',   key: 'tickets',   Icon: IconTicket },
  { to: '/documents', key: 'documents', Icon: IconFile },
  { to: '/uniform',   key: 'uniform',   Icon: IconShirt },
  { to: '/assets',    key: 'assets',    Icon: IconBox },
  { to: '/requests',  key: 'requests',  Icon: IconInbox },
  // Only meaningful for the ~1-2 confirmer_scope people per base/department
  // (see Logistics/DECISIONS.md) — shown to everyone like the rest of this
  // static nav, page renders an empty state for non-confirmers rather than
  // hiding conditionally (no per-user nav filtering exists in this shell yet).
  { to: '/confirmations', key: 'confirmations', Icon: IconCheckSquare },
  { to: '/gdpr',      key: 'data',      Icon: IconShield },
];
// Shown only to station approvers (GET /approvers/me returns at least one station).
const APPROVALS_NAV = { to: '/approvals', key: 'approvals', Icon: IconCheckSquare };

function NavBadge({ n, floating }) {
  return (
    <span aria-label={`${n}`} data-testid="approvals-badge"
          className={`inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white ${floating ? 'absolute top-0.5 right-2' : 'ml-auto'}`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

export default function PortalShell() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [stations, setStations] = useState(null);   // null = still loading; [] = not a designated approver
  const [hasRole, setHasRole] = useState(false);    // station-management role approver (no designation needed)
  const [pending, setPending] = useState(0);        // decisions waiting for this user

  useEffect(() => {
    let alive = true;
    api.get('/approvers/me').then(({ data }) => { if (alive) { setStations(data.stations || []); setHasRole(!!data.role); } }).catch(() => { if (alive) setStations([]); });
    return () => { alive = false; };
  }, []);

  const isApprover = (stations?.length ?? 0) > 0 || hasRole;
  const refreshCounts = useCallback(() => {
    if (!isApprover) return Promise.resolve();
    return api.get('/absences/counts').then(({ data }) => setPending(data.inbox || 0)).catch(() => {});
  }, [isApprover]);
  useEffect(() => {
    if (!isApprover) return undefined;
    refreshCounts();
    const timer = setInterval(refreshCounts, 60000);
    return () => clearInterval(timer);
  }, [isApprover, refreshCounts, pathname]);
  const nav = isApprover ? [...NAV.slice(0, 4), APPROVALS_NAV, ...NAV.slice(4)] : NAV;

  // Capture the install prompt so we can offer an in-app "Instalar" button.
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  // Unread announcements → header bell badge. Poll + refresh on navigation
  // (opening a comm marks it read, which should drop the count).
  useEffect(() => {
    let alive = true;
    const load = () => api.get('/comms/unread-count').then(({ data }) => { if (alive) setUnread(data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);

  const doLogout = async () => { await logout(); navigate('/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-glk-light">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <img src={logoWordmark} alt="GroundLink III Handling" className="h-7 w-auto" />
          <div className="flex items-center gap-1.5">
            <button onClick={() => setLang(lang === 'pt' ? 'en' : 'pt')} className="btn-ghost !px-2.5 !py-1.5 text-xs font-semibold" aria-label="Language">
              {lang === 'pt' ? 'EN' : 'PT'}
            </button>
            {installPrompt && (
              <button onClick={install} className="btn-ghost !px-2.5 !py-1.5 text-xs" aria-label={t('common.install')}>
                {t('common.install')}
              </button>
            )}
            <button onClick={() => navigate('/comms')} className="relative btn-ghost !px-2.5 !py-1.5" aria-label={t('nav.comms')}>
              <IconBell size={18} />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-[1.1rem] h-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
            <button onClick={doLogout} className="btn-ghost !px-2.5 !py-1.5" aria-label={t('common.logout')}>
              <IconLogout size={18} />
              <span className="hidden sm:inline text-xs">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto md:flex md:gap-6 md:px-4">
        {/* Desktop side rail */}
        <nav className="hidden md:block w-48 shrink-0 py-6">
          <div className="sticky top-20 space-y-1">
            {nav.map(({ to, key, Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? 'bg-glk-green/10 text-glk-green' : 'text-gray-500 hover:bg-gray-100 hover:text-glk-dark'
                  }`}>
                <Icon size={20} />{t(`nav.${key}`)}
                {key === 'approvals' && pending > 0 && <NavBadge n={pending} />}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Page content */}
        <main className="flex-1 min-w-0 pb-24 md:pb-8">
          <motion.div
            key={typeof window !== 'undefined' ? window.location.pathname : 'page'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet context={{ user, stations, pending, refreshCounts }} />
          </motion.div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur border-t border-gray-200 flex overflow-x-auto"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {nav.map(({ to, key, Icon, end }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) => `navitem relative shrink-0 min-w-[80px] max-w-[96px] min-h-[44px] px-1 text-center leading-tight ${isActive ? 'navitem-active' : ''}`}>
            <Icon size={22} />{t(`nav.${key}`)}
            {key === 'approvals' && pending > 0 && <NavBadge n={pending} floating />}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
